import { useCallback, useEffect, useState } from "react";
import { useAdsWorkspaceStore } from "@/features/ads";
import { urlToAttachedImage } from "@/shared/lib/urlToAttachedImage";
import {
  formatChatAttachmentFailures,
  uploadChatAttachmentFile,
  type ChatAttachedImage,
} from "../lib/chatAttachments";

export type PendingAttachmentDrainTarget = "none" | "marcus" | "active-chat";

interface UseChatAttachmentsOptions {
  allowVideo?: boolean;
  drainPendingAttachment?: PendingAttachmentDrainTarget;
}

export function useChatAttachments({
  allowVideo = true,
  drainPendingAttachment = "none",
}: UseChatAttachmentsOptions = {}) {
  const [attachedImages, setAttachedImages] = useState<ChatAttachedImage[]>([]);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  const pendingAttachment = useAdsWorkspaceStore((s) => s.pendingChatImageAttachment);
  const selectedChatAgent = useAdsWorkspaceStore((s) => s.selectedChatAgent);

  const addAttachment = useCallback((attachment: ChatAttachedImage) => {
    setAttachedImages((prev) =>
      prev.some((existing) => existing.url === attachment.url) ? prev : [...prev, attachment],
    );
  }, []);

  const clearAttachments = useCallback(() => {
    setAttachedImages([]);
  }, []);

  const clearAttachmentError = useCallback(() => {
    setAttachmentError(null);
  }, []);

  const removeAttachment = useCallback((url: string) => {
    setAttachedImages((prev) => prev.filter((img) => img.url !== url));
  }, []);

  const handleImageAttach = useCallback(
    async (files: FileList) => {
      const selectedFiles = Array.from(files);
      if (selectedFiles.length === 0) return;

      setAttachmentError(null);
      setIsUploadingImage(true);
      const failures: string[] = [];

      try {
        for (const file of selectedFiles) {
          const { attachment, failure } = await uploadChatAttachmentFile(file, { allowVideo });
          if (attachment) {
            addAttachment(attachment);
          }
          if (failure) {
            failures.push(failure);
          }
        }
      } finally {
        setIsUploadingImage(false);
      }

      const formattedFailures = formatChatAttachmentFailures(failures);
      if (formattedFailures) {
        setAttachmentError(formattedFailures);
      }
    },
    [addAttachment, allowVideo],
  );

  useEffect(() => {
    if (!pendingAttachment || drainPendingAttachment === "none") return;
    if (drainPendingAttachment === "marcus" && selectedChatAgent !== null) return;

    const { url, filename, mediaType, kind } = pendingAttachment;
    let cancelled = false;

    urlToAttachedImage({ url, filename, mediaType, kind })
      .then((attachment) => {
        if (cancelled) return;
        addAttachment(attachment);
        useAdsWorkspaceStore.getState().clearPendingChatImageAttachment();
      })
      .catch((error) => {
        if (cancelled) return;
        const message = error instanceof Error ? error.message : "Unable to attach media";
        setAttachmentError(`${filename}: ${message}`);
        useAdsWorkspaceStore.getState().clearPendingChatImageAttachment();
      });

    return () => {
      cancelled = true;
    };
  }, [addAttachment, drainPendingAttachment, pendingAttachment, selectedChatAgent]);

  return {
    attachedImages,
    isUploadingImage,
    attachmentError,
    handleImageAttach,
    removeAttachment,
    clearAttachments,
    clearAttachmentError,
  };
}
